import { lazy, Suspense } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { Reveal, MaskedLine, Eyebrow } from "../components/Reveal";
import Marquee from "../components/Marquee";
import Globe from "../components/Globe";
import CityClocks from "../components/CityClocks";
import { useLang } from "../i18n/LanguageContext";

const HeroGlobe = lazy(() => import("../components/HeroGlobe"));

const HANDSHAKE_IMG = "https://images.unsplash.com/photo-1638262052640-82e94d64664a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwyfHxoYW5kc2hha2UlMjBidXNpbmVzcyUyMGRldGFpbHxlbnwwfHx8fDE3OTAzMjc0ODJ8MA&ixlib=rb-4.1.0&q=85";

export default function Home() {
  const { t } = useLang();

  const LEVELS = [
    { n: "01", name: t("lvl1_name"), alloc: t("lvl1_alloc"), text: t("lvl1_text") },
    { n: "02", name: t("lvl2_name"), alloc: t("lvl2_alloc"), text: t("lvl2_text") },
    { n: "03", name: t("lvl3_name"), alloc: t("lvl3_alloc"), text: t("lvl3_text") },
    { n: "04", name: t("lvl4_name"), alloc: t("lvl4_alloc"), text: t("lvl4_text") },
  ];

  const STEPS = [
    { n: "01", title: t("step1_t"), text: t("step1_x") },
    { n: "02", title: t("step2_t"), text: t("step2_x") },
    { n: "03", title: t("step3_t"), text: t("step3_x") },
    { n: "04", title: t("step4_t"), text: t("step4_x") },
  ];

  const FLOW = [t("met_f1"), t("met_f2"), t("met_f3"), t("met_f4"), t("met_f5")];

  return (
    <div data-testid="home-page">
      {/* 01 — HERO */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 min-h-[86vh] grid grid-cols-1 lg:grid-cols-12 gap-8 items-center py-20 lg:py-0">
          <div className="relative z-10 lg:col-span-7 min-w-0">
            <MaskedLine delay={0.1}>
              <span className="eyebrow">{t("hero_eyebrow")}</span>
            </MaskedLine>
            <h1 className="mt-8 text-6xl sm:text-7xl lg:text-8xl leading-[1.02]" data-testid="hero-title">
              <MaskedLine delay={0.25}>
                <>Liberty <em className="italic text-gold">CP</em></>
              </MaskedLine>
            </h1>
            <Reveal delay={0.45}>
              <p className="mt-4 font-serif text-2xl sm:text-3xl text-ink-muted italic" data-testid="hero-tagline">
                {t("hero_tagline")}
              </p>
            </Reveal>
            <Reveal delay={0.55}>
              <p className="mt-6 max-w-xl text-lg text-ink-muted leading-relaxed" data-testid="hero-subtitle">
                {t("hero_sub")}
              </p>
            </Reveal>
            <Reveal delay={0.7} className="mt-10 flex flex-wrap gap-4">
              <a href="#liberty" className="btn-primary" data-testid="hero-cta-discover">{t("hero_cta1")}</a>
              <Link to="/accedi?mode=registrati" className="btn-outline" data-testid="hero-cta-access">{t("hero_cta2")}</Link>
            </Reveal>
            <Reveal delay={0.85}>
              <p className="mt-14 text-xs font-semibold uppercase tracking-[0.35em] text-pine/70" data-testid="hero-microline">
                {t("hero_micro")}
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-5 min-w-0 relative h-[340px] sm:h-[420px] lg:h-[640px] [mask-image:linear-gradient(to_bottom,transparent,black_16%)] lg:[mask-image:linear-gradient(to_right,transparent,black_22%)]">
            <Suspense fallback={<div className="w-full h-full" aria-hidden="true" />}>
              <HeroGlobe className="w-full h-full" />
            </Suspense>
          </div>
        </div>
      </section>

      <CityClocks />

      <Marquee items={[t("marq_1"), t("marq_2"), t("marq_3"), t("marq_4"), t("marq_5"), t("marq_6")]} />

      {/* 02 — LIBERTY */}
      <section id="liberty" className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-24 lg:py-32" data-testid="home-liberty-section">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5 min-w-0">
            <Reveal><Eyebrow>{t("lib_eyebrow")}</Eyebrow></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-6 text-3xl sm:text-4xl lg:text-5xl leading-tight" data-testid="liberty-title">
                {t("lib_title_pre")} <em className="italic text-gold">{t("lib_title_em")}</em>.
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-8 text-lg text-ink-muted leading-relaxed">{t("lib_p1")}</p>
              <p className="mt-5 text-lg text-ink-muted leading-relaxed">{t("lib_p2")}</p>
              <p className="mt-5 text-lg text-ink-muted leading-relaxed">{t("lib_p3")}</p>
            </Reveal>
            <Reveal delay={0.3} className="mt-10 flex flex-wrap items-center gap-6">
              <Link to="/liberty" className="link-arrow" data-testid="liberty-cta">
                {t("lib_cta")} <ArrowRight size={16} className="rtl:rotate-180" />
              </Link>
              <a
                href="https://libertyprivateoffice.com"
                target="_blank"
                rel="noopener noreferrer"
                className="link-arrow !text-ink-muted"
                data-testid="liberty-external-link"
              >
                libertyprivateoffice.com <ArrowUpRight size={16} />
              </a>
            </Reveal>
          </div>
          <div className="lg:col-span-7 min-w-0">
            <ol className="border-t border-pine/15">
              {LEVELS.map((l, i) => (
                <Reveal key={l.n} delay={i * 0.08}>
                  <li className="group grid grid-cols-[auto_1fr] gap-6 sm:gap-10 items-baseline py-7 border-b border-pine/15 transition-colors duration-300 hover:bg-surface" data-testid={`level-${l.n}`}>
                    <span className="font-serif text-xl text-gold">{l.n}</span>
                    <div>
                      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
                        <h3 className="font-serif text-2xl sm:text-3xl tracking-[0.04em] text-ink group-hover:text-pine transition-colors duration-300">{l.name}</h3>
                        <span className="text-xs font-semibold uppercase tracking-[0.22em] text-gold">{l.alloc}</span>
                      </div>
                      <p className="mt-2 text-base text-ink-muted max-w-md">{l.text}</p>
                    </div>
                  </li>
                </Reveal>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* 03 — IL PROGRAMMA */}
      <section id="programma" className="bg-surface border-y border-pine/10" data-testid="home-program-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-24 lg:py-32 grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7 min-w-0">
            <Reveal><Eyebrow>{t("prog_eyebrow")}</Eyebrow></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-6 text-3xl sm:text-4xl lg:text-5xl leading-tight" data-testid="program-title">
                {t("prog_title_pre")} <em className="italic text-gold">{t("prog_title_em")}</em>.
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-8 text-lg text-ink-muted leading-relaxed max-w-2xl">{t("prog_p1")}</p>
              <p className="mt-5 text-lg text-ink-muted leading-relaxed max-w-2xl">{t("prog_p2")}</p>
            </Reveal>
            <Reveal delay={0.3} className="mt-10">
              <Link to="/liberty" className="link-arrow" data-testid="program-cta">
                {t("prog_cta")} <ArrowRight size={16} className="rtl:rotate-180" />
              </Link>
            </Reveal>
          </div>
          <Reveal delay={0.25} className="lg:col-span-5 min-w-0">
            <div className="card-surface p-8 sm:p-10" data-testid="program-objective-card">
              <p className="text-xs font-semibold uppercase tracking-[0.3em] text-ink-muted">{t("prog_card_label")}</p>
              <p className="mt-4 font-serif text-5xl sm:text-6xl text-pine" data-testid="program-objective-value">1,2–1,5%</p>
              <p className="mt-1 text-lg text-ink-muted">{t("prog_card_sub")}</p>
              <div className="mt-8 h-px bg-gold/40" aria-hidden="true" />
              <p className="mt-6 text-sm text-ink-muted leading-relaxed">{t("prog_card_note")}</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 04 — COME FUNZIONA */}
      <section className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-24 lg:py-32" data-testid="home-how-section">
        <Reveal><Eyebrow>{t("how_eyebrow")}</Eyebrow></Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-6 text-3xl sm:text-4xl lg:text-5xl leading-tight" data-testid="how-title">
            {t("how_title_pre")}<em className="italic text-gold">{t("how_title_em")}</em>.
          </h2>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="mt-5 text-lg text-ink-muted">{t("how_sub")}</p>
        </Reveal>
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-pine/10 border border-pine/10">
          {STEPS.map((s, i) => (
            <Reveal key={s.n} delay={i * 0.08} className="h-full">
              <div className="bg-cream h-full p-8 flex flex-col transition-colors duration-300 hover:bg-surface" data-testid={`step-${s.n}`}>
                <span className="font-serif text-3xl text-gold">{s.n}</span>
                <h3 className="mt-6 font-serif text-2xl text-ink">{s.title}</h3>
                <p className="mt-3 text-base text-ink-muted leading-relaxed">{s.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* 05 — IL METODO */}
      <section className="bg-pine text-cream" data-testid="home-capital-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-24 lg:py-32 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <Reveal><Eyebrow className="!text-gold">{t("met_eyebrow")}</Eyebrow></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-6 text-3xl sm:text-4xl lg:text-5xl leading-tight text-cream" data-testid="capital-title">
                {t("met_title_pre")} <em className="italic text-gold">{t("met_title_em")}</em>.
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-8 text-lg text-cream/80 leading-relaxed max-w-xl">{t("met_text")}</p>
            </Reveal>
          </div>
          <Reveal delay={0.2}>
            <div className="relative ps-8" data-testid="capital-flow">
              <span className="absolute start-0 top-2 bottom-2 w-px bg-gold/60" aria-hidden="true" />
              {FLOW.map((label) => (
                <div key={label} className="relative py-5">
                  <span className="absolute -start-8 top-1/2 -translate-y-1/2 h-2 w-2 rotate-45 bg-gold" aria-hidden="true" />
                  <p className="font-serif text-2xl sm:text-3xl tracking-[0.12em] text-cream">{label}</p>
                </div>
              ))}
            </div>
            <p className="mt-6 text-sm text-cream/70 leading-relaxed max-w-md">{t("met_note")}</p>
          </Reveal>
        </div>
      </section>

      {/* 06 — AREA PERSONALE */}
      <section className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-24 lg:py-32" data-testid="home-area-section">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5 min-w-0">
            <Reveal><Eyebrow>{t("area_eyebrow")}</Eyebrow></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-6 text-3xl sm:text-4xl lg:text-5xl leading-tight" data-testid="area-title">
                {t("area_title_pre")} <em className="italic text-gold">{t("area_title_em")}</em>{t("area_title_post")}
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-8 text-lg text-ink-muted leading-relaxed">{t("area_text")}</p>
            </Reveal>
            <Reveal delay={0.3} className="mt-10">
              <Link to="/accedi" className="btn-outline" data-testid="area-cta">{t("area_cta")}</Link>
            </Reveal>
          </div>
          <Reveal delay={0.25} className="lg:col-span-7 min-w-0">
            <div className="card-surface p-8 sm:p-12 relative" data-testid="area-preview-card">
              <span className="absolute -top-3.5 end-8 bg-gold text-ink text-[0.65rem] font-bold uppercase tracking-[0.3em] px-4 py-1.5" data-testid="area-preview-label">
                {t("area_badge")}
              </span>
              <div className="grid grid-cols-2 gap-x-8 gap-y-10">
                {[
                  { label: t("area_s1"), value: "€ 50.000" },
                  { label: t("area_s2"), value: "€ 2.840" },
                  { label: t("area_s3"), value: "€ 52.840" },
                  { label: t("area_s4"), value: "€ 0" },
                ].map((s) => (
                  <div key={s.label}>
                    <p className="text-xs font-semibold uppercase tracking-[0.25em] text-ink-muted">{s.label}</p>
                    <p className="mt-3 font-serif text-3xl sm:text-4xl text-ink">{s.value}</p>
                  </div>
                ))}
              </div>
              <div className="mt-10 pt-6 border-t border-pine/10">
                <p className="text-xs text-ink-muted">{t("area_note")}</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* GLOBAL ACCESS */}
      <section className="bg-surface border-y border-pine/10" data-testid="home-global-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-24 lg:py-32 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <Globe className="w-full" />
          </Reveal>
          <div>
            <Reveal><Eyebrow>{t("glob_eyebrow")}</Eyebrow></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-6 text-3xl sm:text-4xl lg:text-5xl leading-tight" data-testid="global-title">
                Global <em className="italic text-gold">Access</em>.
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-8 text-lg text-ink-muted leading-relaxed max-w-xl" data-testid="global-text">
                {t("glob_text")}
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* 07 — CHIUSURA */}
      <section className="relative overflow-hidden border-t border-pine/10" data-testid="home-closing-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pt-24 lg:pt-32 pb-10 relative z-10">
          <Reveal>
            <div className="max-w-2xl">
              <Eyebrow>{t("part_eyebrow")}</Eyebrow>
              <p className="mt-6 text-lg text-ink-muted leading-relaxed">{t("part_text")}</p>
              <Link to="/partner" className="link-arrow mt-5" data-testid="partner-cta">
                {t("part_cta")} <ArrowRight size={16} className="rtl:rotate-180" />
              </Link>
            </div>
          </Reveal>
        </div>
        <div className="relative mt-16 lg:mt-24">
          <img
            src={HANDSHAKE_IMG}
            alt={t("close_alt")}
            className="absolute inset-0 w-full h-full object-cover"
            loading="lazy"
            data-testid="closing-handshake-img"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-pine/95 via-pine/85 to-pine-deep/70" aria-hidden="true" />
          <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-24 lg:py-32">
            <Reveal>
              <h2 className="text-4xl sm:text-5xl lg:text-6xl leading-[1.1] text-cream" data-testid="closing-title">
                {t("close_title_pre")} <em className="italic text-gold">{t("close_title_em")}</em>.
              </h2>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-8 text-lg text-cream/85 leading-relaxed max-w-xl">{t("close_text")}</p>
            </Reveal>
            <Reveal delay={0.25} className="mt-10">
              <Link to="/contatti" className="btn-primary" data-testid="closing-cta">{t("close_cta")}</Link>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
